// src/contexts/WalletContext.jsx
import React, { createContext, useState, useContext, useEffect, useCallback } from "react";
import { BrowserProvider, ethers } from "ethers"; // ethers is needed for BigInt usually
import { connectWalletAndGetProfile } from '../services/agentService'; // Ensure this path is correct

const WalletContext = createContext(undefined); // It's good practice to provide a default value

export const useWallet = () => {
  const context = useContext(WalletContext);
  if (context === undefined) {
    throw new Error('useWallet must be used within a WalletProvider');
  }
  return context;
};

export const WalletProvider = ({ children }) => {
  const [currentAccount, setCurrentAccount] = useState(null); // Should store the address string
  const [signer, setSigner] = useState(null); // Should store the Signer object
  const [provider, setProvider] = useState(null);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [userProfile, setUserProfile] = useState(null); // Optional: to store profile from backend

  const clearError = useCallback(() => setError(""), []);

  // Function to update account and signer together
  const updateAccountAndSigner = useCallback(async (newAddress, ethProvider) => {
    if (newAddress) {
      setCurrentAccount(newAddress);
      try {
        const newSignerInstance = await ethProvider.getSigner(newAddress);
        setSigner(newSignerInstance);
        setError(""); // Clear previous errors on successful account set
        console.log("WalletContext: Account and Signer updated:", newAddress);
        
        // Fetch profile when account is set/changed
        try {
          const profile = await connectWalletAndGetProfile(newAddress, null); // Assuming null for displayName initially
          setUserProfile(profile);
          console.log("WalletContext: User profile updated/fetched:", profile);
        } catch (profileError) {
          console.error("WalletContext: Error fetching/creating user profile:", profileError);
          // Decide if this should set a user-facing error or just log
          // setError("Could not sync wallet with server profile. Wallet connected locally.");
        }
      } catch (e) {
        console.error("WalletContext: Error getting signer for address:", newAddress, e);
        setCurrentAccount(null); // Rollback account if signer fails
        setSigner(null);
        setError("Could not set up signer for the account.");
      }
    } else {
      setCurrentAccount(null);
      setSigner(null);
      setUserProfile(null);
      console.log("WalletContext: Account and Signer cleared (disconnected).");
    }
  }, []); // No dependencies, it uses its own arguments

  // Effect for initializing provider and listeners
  useEffect(() => {
    let isMounted = true;
    if (typeof window.ethereum !== 'undefined') {
      const ethProvider = new BrowserProvider(window.ethereum);
      if (isMounted) setProvider(ethProvider);

      // Check for already connected accounts on initial load
      const checkInitialConnection = async () => {
        try {
          const accounts = await ethProvider.send("eth_accounts", []); // More reliable for checking existing connection
          if (accounts.length > 0 && isMounted) {
            await updateAccountAndSigner(accounts[0], ethProvider);
          } else if (isMounted) {
            console.log("WalletContext: No initially authorized account found.");
          }
        } catch (err) {
          console.error("WalletContext: Error checking initial wallet connection:", err);
          if (isMounted) setError("Failed to check initial wallet connection.");
        }
      };
      checkInitialConnection();

      const handleAccountsChanged = (accounts) => {
        console.log("WalletContext: accountsChanged event", accounts);
        if (isMounted) {
          if (accounts.length > 0) {
            updateAccountAndSigner(accounts[0], ethProvider);
          } else {
            updateAccountAndSigner(null, ethProvider); // Disconnected
            setError("Wallet disconnected. Please connect again.");
          }
        }
      };

      const handleChainChanged = (_chainId) => {
        console.log("WalletContext: chainChanged event", _chainId);
        if (isMounted) window.location.reload(); // Simple way to handle chain changes
      };

      window.ethereum.on("accountsChanged", handleAccountsChanged);
      window.ethereum.on("chainChanged", handleChainChanged);

      return () => {
        isMounted = false;
        if (window.ethereum.removeListener) {
          window.ethereum.removeListener("accountsChanged", handleAccountsChanged);
          window.ethereum.removeListener("chainChanged", handleChainChanged);
        }
      };
    } else {
      if (isMounted) setError("MetaMask not detected. Please install it to use this app.");
    }
    return () => { isMounted = false; };
  }, [updateAccountAndSigner]); // updateAccountAndSigner is stable due to its own useCallback

  const connectWallet = useCallback(async () => {
    if (!window.ethereum) {
      setError("MetaMask not detected. Please install it.");
      return;
    }
    // Provider should be set by useEffect, but as a fallback:
    const currentProvider = provider || new BrowserProvider(window.ethereum);
    if (!provider) setProvider(currentProvider);

    setIsLoading(true);
    clearError(); // Clear previous errors
    try {
      const accounts = await currentProvider.send("eth_requestAccounts", []);
      if (accounts.length > 0) {
        await updateAccountAndSigner(accounts[0], currentProvider);
      } else {
        setError("No accounts found after connection request.");
      }
    } catch (err) {
      console.error("WalletContext: Error connecting wallet:", err);
      if (err.code === 4001) {
        setError("Request to connect wallet was rejected by the user.");
      } else if (err.message?.includes("already pending")) {
        setError("Connection request is already pending. Please check your MetaMask extension.");
      } else {
        setError("Failed to connect wallet: " + (err.message || "Unknown error"));
      }
      // Ensure account state is nullified if connection fails badly
      setCurrentAccount(null);
      setSigner(null);
      setUserProfile(null);
    } finally {
      setIsLoading(false);
    }
  }, [provider, updateAccountAndSigner, clearError]);

  const disconnectWallet = useCallback(() => {
    updateAccountAndSigner(null, provider); // Clear account and signer
    // Optionally, you might want to inform the backend or clear other session data
    console.log("WalletContext: User initiated disconnect.");
  }, [provider, updateAccountAndSigner]);

  const signMessage = useCallback(async (message) => {
    if (!signer) {
      setError("Signer not available. Please connect your wallet first.");
      return null;
    }
    clearError();
    try {
      const signature = await signer.signMessage(message);
      return signature;
    } catch (err) {
      console.error("WalletContext: Error signing message:", err);
      setError("Failed to sign message: " + (err.message || "Unknown error"));
      return null;
    }
  }, [signer, clearError]);

  const value = {
    currentAccount,
    provider,
    signer,
    userProfile, // Expose userProfile if you set it
    error,
    isLoading,
    connectWallet,
    disconnectWallet,
    signMessage,
    clearError, // Expose clearError if components need to clear errors manually
    setError,   // Expose setError if components need to set context-level errors
  };

  return (
    <WalletContext.Provider value={value}>{children}</WalletContext.Provider>
  );
};